/**
 * McpDemo — empty-state terminal mockup for the "connect Zibby to your
 * personal AI agent" slide.
 *
 * Sibling of WorkflowDemo / AppsDemo: same typewriter engine, same gss-*
 * CSS classes, same BrowserWindow chrome. Renders as the 2nd slide of the
 * DemoCarousel on the Pipelines + Apps pages.
 *
 * Two bits of context drive what's shown:
 *   - agent tabs (top-right of the terminal titlebar): pick Claude / Cursor
 *     / Codex / Gemini → the install command + config path retype for that
 *     agent's exact `--agent <flag>` invocation (no `--all`).
 *   - `context` prop ('pipeline' | 'apps'): the "now just ask your agent"
 *     examples match the page — workflow verbs on Pipelines, app verbs on
 *     Apps. (Showing app commands on the pipelines page reads wrong.)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { sparklesOutline } from 'ionicons/icons';
import BrowserWindow from '../../atoms/BrowserWindow';
import './TestRuns.css';

// `flag` = canonical agent id (tab identity); `cmd` = the flag shown in the
// install command. Claude shows the short `claude` alias (the CLI accepts it
// → claude-code) since that reads cleaner than `claude-code`.
const AGENTS = [
  { flag: 'claude-code', cmd: 'claude', label: 'Claude', configPath: '~/.claude/settings.json' },
  { flag: 'cursor', cmd: 'cursor', label: 'Cursor', configPath: '~/.cursor/mcp.json' },
  { flag: 'codex', cmd: 'codex', label: 'Codex', configPath: '~/.codex/config.toml' },
  { flag: 'gemini', cmd: 'gemini', label: 'Gemini', configPath: '~/.gemini/settings.json' },
];

// "now just ask your agent" examples — context-specific so the pipelines
// page shows workflow verbs and the apps page shows app verbs.
const ASK_EXAMPLES = {
  pipeline: [
    { text: '▸ "deploy the my-pipeline workflow"  → zibby_workflow_deploy', cls: 'gss-success' },
    { text: '▸ "trigger my-pipeline -t"           → zibby_workflow_trigger', cls: 'gss-success' },
    { text: '▸ "tail logs for run 8a3f9e2c"       → zibby_logs_tail', cls: 'gss-success' },
  ],
  apps: [
    { text: '▸ "deploy grafana to my project"     → zibby_app_deploy', cls: 'gss-success' },
    { text: '▸ "show status of my dashboards app" → zibby_app_status', cls: 'gss-success' },
    { text: '▸ "tail logs for 8a3f9e2c"           → zibby_logs_tail', cls: 'gss-success' },
  ],
};

function buildSteps(agent, context) {
  return [
    {
      command: `npx -y @zibby/cli@latest mcp install --agent ${agent.cmd}`,
      output: [
        { text: '⚙ Reading your `zibby login` session…', cls: '' },
        { text: '⚙ Minting a scoped PAT…', cls: '' },
        { text: `✓ Configured ${agent.label} → ${agent.configPath}`, cls: 'gss-success' },
        { text: `→ Restart ${agent.label} to load the zibby_* tools`, cls: 'gss-link-line' },
      ],
    },
    {
      command: '# now just ask your agent — it drives Zibby directly',
      output: ASK_EXAMPLES[context] || ASK_EXAMPLES.apps,
    },
  ];
}

const CHAR_SPEED = 18;
const OUTPUT_LINE_DELAY = 120;
const STEP_PAUSE = 300;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const McpDemo = ({ context = 'apps' }) => {
  const [agentFlag, setAgentFlag] = useState(AGENTS[0].flag);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);
  const firstRunRef = useRef(true);

  const agent = AGENTS.find((a) => a.flag === agentFlag) || AGENTS[0];
  const steps = useMemo(() => buildSteps(agent, context), [agent, context]);

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        if (!cancelRef.current) resolve();
      }, ms);
      timeoutsRef.current.push(t);
    });
  }, []);

  useEffect(() => {
    cancelRef.current = false;
    // Switching agents retypes from scratch — clear the old lines so the
    // new command/config animate in cleanly.
    setVisibleSteps([]);

    const animate = async () => {
      // Wait for the carousel/wrapper fade-in on the FIRST run; agent
      // re-types after that should feel snappy, not laggy.
      await delay(firstRunRef.current ? 380 : 60);
      firstRunRef.current = false;
      for (let si = 0; si < steps.length; si++) {
        if (cancelRef.current) return;
        const step = steps[si];

        for (let ci = 1; ci <= step.command.length; ci++) {
          if (cancelRef.current) return;
          setVisibleSteps((prev) => {
            const next = [...prev];
            next[si] = { commandText: step.command.slice(0, ci), outputLines: [], done: false };
            return next;
          });
          await delay(CHAR_SPEED);
        }

        for (let oi = 0; oi < step.output.length; oi++) {
          if (cancelRef.current) return;
          await delay(OUTPUT_LINE_DELAY);
          setVisibleSteps((prev) => {
            const next = [...prev];
            const current = { ...next[si] };
            current.outputLines = [...current.outputLines, step.output[oi]];
            next[si] = current;
            return next;
          });
        }

        setVisibleSteps((prev) => {
          const next = [...prev];
          next[si] = { ...next[si], done: true };
          return next;
        });

        if (si < steps.length - 1) {
          await delay(STEP_PAUSE);
        }
      }
    };

    animate();
    return clearAllTimeouts;
  }, [delay, clearAllTimeouts, steps]);

  const agentTabs = (
    <div className="mcp-agent-tabs">
      {AGENTS.map((a) => (
        <button
          key={a.flag}
          type="button"
          className={`mcp-agent-tab ${agentFlag === a.flag ? 'is-active' : ''}`}
          onClick={() => setAgentFlag(a.flag)}
        >
          {a.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="gss-wrapper">
      <div className="gss-header">
        <IonIcon icon={sparklesOutline} className="gss-header-icon" />
        <span className="gss-header-text">
          Connect Zibby to Claude, Cursor or Codex — one MCP, every agent.{' '}
          <Link className="gss-header-link" to="/integrations">
            Manage agents
          </Link>.
        </span>
      </div>
      <BrowserWindow variant="dark" className="gss-terminal" titlebarRight={agentTabs}>
        <div className="gss-terminal-body">
          {steps.map((step, si) => {
            const vis = visibleSteps[si];
            if (!vis) return null;
            return (
              <div key={si} className="gss-step">
                <div className="gss-command">
                  <span className="gss-prompt">$</span>
                  <span className="gss-command-text">
                    {vis.commandText}
                    {!vis.done && vis.outputLines.length === 0 && <span className="gss-cursor">|</span>}
                  </span>
                  {vis.done && (
                    <button
                      className="gss-copy-btn"
                      onClick={() => navigator.clipboard?.writeText(step.command)}
                      aria-label="Copy command"
                    >
                      <CopyIcon />
                    </button>
                  )}
                </div>
                {vis.outputLines.length > 0 && (
                  <div className="gss-output">
                    {vis.outputLines.map((line, li) => (
                      <span key={li} className={`gss-output-line gss-indent ${line.cls}`}>
                        {line.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </BrowserWindow>
    </div>
  );
};

export default McpDemo;
